<template>
<v-card>
  <v-card-title class="primary white--text">
    <span class="headline">{{ title }}</span>
    <v-spacer/>
    <Icon
      iconColor="white"
      icon="mdi-close"
      dataTestId="product-close-button"
      :small="false"
      :large="true"
      :tooltipText="$t('close')"
      @icon-clicked="$emit('close')"/>
  </v-card-title>

  <v-card-text>
    <FormWrapper
      ref="form"
      formRef="productForm"
      testId="save-product"
      :lazyValidation="true"
      :buttonText="saveBtnText"
      @submit="saveProduct">

      <v-container fluid>
        <v-row>
          <v-col cols="12" sm="6">
            <LoadableComponent :loading="loading">
              <v-text-field
                v-model="product.name"
                ref="firstField"
                color="black"
                counter="64"
                maxlength="64"
                :rules="[rules.required]"
                :label="$t('name')"/>
            </LoadableComponent>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <LoadableComponent :loading="loading">
              <v-text-field
                v-model="product.code"
                color="black"
                counter="10"
                maxlength="10"
                :label="$t('code')"/>
            </LoadableComponent>
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <LoadableComponent :loading="loading">
              <v-text-field
                :disabled="disableConversionRateW2V"
                :hint="conversionRateW2VHint"
                :persistentHint="disableConversionRateW2V"
                v-model="product.conversionRateW2V"
                color="black"
                type="number"
                hide-spin-buttons
                :label="$t('conversionRateW2V')"
                :rules="[rules.positiveDecimal, rules.validConversionRate]"/>
            </LoadableComponent>
          </v-col>

          <v-col cols="12" sm="6">
            <LoadableComponent :loading="loading">
              <v-select
                :menu-props="{ offsetY: true }"
                :items="woodTypes"
                :rules="[rules.required]"
                :label="$t('woodType')"
                item-color="primary"
                color="black"
                v-model="product.woodType"/>
            </LoadableComponent>
          </v-col>

          <v-col cols="12" sm="6">
            <LoadableComponent :loading="loading">
              <v-select
                :menu-props="{ offsetY: true }"
                :items="timberTypes"
                :rules="[rules.required]"
                item-color="primary"
                color="black"
                :label="$t('timberType')"
                v-model="product.timberType"/>
            </LoadableComponent>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <LoadableComponent :loading="loading">
              <SpeciesAutocomplete
                :items="allSpecies"
                :loading="loading"
                :label="$t('species')"
                :speciesList="allSpecies"
                :initSelection="initialSpeciesSelection"
                multiple
                @species-chosen="speciesChosen"
              />
            </LoadableComponent>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <LoadableComponent :loading="loading">
              <v-select
                :menu-props="{ offsetY: true }"
                multiple
                chips
                deletable-chips
                item-text="name"
                item-value="gradeId"
                :items="allGrades"
                :label="$t('grades')"
                :prependIcon="IconHelpers.getIconForObjectType('grade')"
                v-model="product.gradeIds"/>
            </LoadableComponent>
          </v-col>

          <v-col cols="12" sm="6" md="4">
            <LoadableComponent :loading="loading">
              <v-select
                :menu-props="{ offsetY: true }"
                multiple
                chips
                deletable-chips
                item-text="name"
                item-value="endUseSortId"
                :items="allEndUseSorts"
                :label="$t('endUseSorts')"
                :prependIcon="IconHelpers.getIconForObjectType('endUseSort')"
                v-model="product.endUseSortIds"/>
            </LoadableComponent>
          </v-col>
        </v-row>
      </v-container>

      <template #right-action>
        <v-checkbox
          v-model="product.collectPieceInfo"
          color="primary"
          class="mr-4"
          :label="$t('requiresPieceEntry')"/>
      </template>
    </FormWrapper>
  </v-card-text>
</v-card>
</template>

<script>
import fieldRules from '@/utils/rules'
import { mapActions } from 'vuex'
import { WoodType, TimberType } from '@/utils/Enumerations.js'
import { mapGetters } from 'vuex/dist/vuex.common.js'
import IconHelpers from '@/utils/IconHelpers'
import { cloneDeep } from 'lodash'

export default {
  name: 'ProductForm',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    LoadableComponent: () => import('@/components/helper/LoadableComponent.vue'),
    SpeciesAutocomplete: () => import('@/components/settings-components/product-settings/species/SpeciesAutocomplete.vue')
  },

  props: {
    isEditing: Boolean,
    propProduct: Object
  },

  data: () => ({
    IconHelpers,
    rules: fieldRules.rules,
    product: {
      productId: -1,
      code: '',
      name: '',
      timberType: '',
      woodType: '',
      conversionRateW2V: '',
      collectPieceInfo: false,
      isByProduct: false,
      speciesIds: [],
      gradeIds: [],
      endUseSortIds: []
    },
    loading: true,
    initialSpeciesSelection: null
  }),

  async mounted () {
    this.loading = true
    await Promise.all([
      this.fetchSpecies(),
      this.fetchGrades(),
      this.fetchEndUseSorts()
    ])
    this.setInitialSpeciesSelection()
    this.loading = false
    setTimeout(_ => {
      this.$refs.firstField.focus()
    }, 0)
  },

  created () {
    if (this.isEditing) {
      Object.assign(this.product, cloneDeep(this.propProduct))
      this.product.woodType = this.woodTypeFromInt(this.propProduct.woodType)
      this.product.timberType = this.timberTypeFromInt(this.propProduct.timberType)
      this.product.speciesIds = this.product.productSpecies.map(s => s.speciesId)
      this.product.gradeIds = this.product.productGrades.map(g => g.gradeId)
      this.product.endUseSortIds = this.product.productEndUseSorts.map(eus => eus.endUseSortId)
    }
  },

  computed: {
    ...mapGetters('species', ['allSpecies']),
    ...mapGetters('grade', ['allGrades']),
    ...mapGetters('end-use-sort', ['allEndUseSorts']),

    title () {
      return this.isEditing ? this.$t('updatingProduct') : this.$t('creatingProduct')
    },

    saveBtnText () {
      return this.isEditing ? this.$t('saveChanges') : this.$t('createNewProduct')
    },

    timberTypes () {
      return TimberType.names
    },

    woodTypes () {
      return WoodType.names
    },

    disableConversionRateW2V () {
      return this.product.speciesIds.length + this.product.gradeIds.length + this.product.endUseSortIds.length > 0
    },

    conversionRateW2VHint () {
      return this.disableConversionRateW2V ? this.$t('conversionRateW2VDisabledHint') : undefined
    }
  },

  watch: {
    disableConversionRateW2V (newVal) {
      if (newVal) {
        this.product.conversionRateW2V = 1
      }
    }
  },

  methods: {
    ...mapActions('product', ['createProduct', 'updateProduct']),
    ...mapActions('species', ['fetchSpecies']),
    ...mapActions('grade', ['fetchGrades']),
    ...mapActions('end-use-sort', ['fetchEndUseSorts']),

    woodTypeFromInt: (x) => WoodType.fromInt(x),
    timberTypeFromInt: (x) => TimberType.fromInt(x),

    async saveProduct () {
      if (!this.$refs.form.$refs.productForm.validate()) {
        return
      }

      const requestObj = cloneDeep(this.product)
      requestObj.conversionRateW2V = parseFloat(requestObj.conversionRateW2V)
      requestObj.woodType = WoodType.toInt(requestObj.woodType)
      requestObj.timberType = TimberType.toInt(requestObj.timberType)

      if (this.isEditing) {
        await this.updateProduct(requestObj)
        this.$emit('product-mutated')
      } else {
        await this.createProduct(requestObj)
        this.$emit('product-mutated')
      }
    },

    speciesChipClosed (species) {
      this.product.speciesIds = this.product.speciesIds.filter(s => s !== species.speciesId)
    },

    speciesIsChosen (species) {
      return this.product.speciesIds.some(s => s === species.speciesId)
    },

    speciesChosen (species) {
      this.product.speciesIds = species.map(s => s.speciesId)
    },

    setInitialSpeciesSelection () {
      this.initialSpeciesSelection = this.product.speciesIds.map(s => this.allSpecies.find(species => species.speciesId === s))
    }
  }
}
</script>
